import moment from 'moment';
import { mealKey } from '../../../redux/food/normalize';

/**
 * The react-beautiful-dnd type shared by everything that can land in a calendar meal slot.
 *
 * rbd only lets a Draggable move between Droppables of the same type, so the slots and every
 * list that feeds them (grocery items and meal ideas) have to use this one type. The dashboard's
 * onDragEnd tells the sources apart by the draggableId prefix instead:
 *
 * - `meal-<uid>`: a meal already on the calendar
 * - `draggableGroceryListItem-<id>`: a grocery list item
 * - `mealIdea-<groupId or "ungrouped">-<ideaId>`: a meal idea, as it appears in one group, since an
 *   idea can sit in several
 */
export const CALENDAR_DRAG_TYPE = 'CALENDAR';

export const MEAL_SLOT_PREFIX = 'mealSlot-';

const DATE_FORMAT = 'YYYY-MM-DD';

export const slotDroppableId = (date, type) => `${MEAL_SLOT_PREFIX}${date.format(DATE_FORMAT)}-${type}`;

export const isSlotDroppableId = (droppableId) => typeof droppableId === 'string' && droppableId.startsWith(MEAL_SLOT_PREFIX);

/** `mealSlot-2026-09-28-3` back into `{ date, type }`, or null for anything else. */
export const parseSlotDroppableId = (droppableId) => {
	if(!isSlotDroppableId(droppableId)) {
		return null;
	}
	const match = droppableId.slice(MEAL_SLOT_PREFIX.length).match(/^(\d{4}-\d{2}-\d{2})-(\d+)$/);
	if(!match) {
		return null;
	}
	return {
		date: moment(match[1], DATE_FORMAT),
		type: Number.parseInt(match[2], 10),
	};
};

export const mealDraggableId = (meal) => `meal-${mealKey(meal)}`;

/** `draggableGroceryListItem-12` into `{ kind: 'draggableGroceryListItem', id: '12' }`. */
export const parseDraggableId = (draggableId) => {
	const dash = draggableId.indexOf('-');
	if(dash === -1) {
		return { kind: draggableId, id: null };
	}
	return { kind: draggableId.slice(0, dash), id: draggableId.slice(dash + 1) };
};

export const MEAL_IDEA_GROUP_PREFIX = 'mealIdeaGroup-';
export const UNGROUPED = 'ungrouped';

export const mealIdeaGroupDroppableId = (groupId) => `${MEAL_IDEA_GROUP_PREFIX}${groupId === null ? UNGROUPED : groupId}`;

export const mealIdeaDraggableId = (groupId, ideaId) => `mealIdea-${groupId === null ? UNGROUPED : groupId}-${ideaId}`;

/** `mealIdea-4-12` into `{ groupId: 4, ideaId: 12 }`, with groupId null for Ungrouped; else null. */
export const parseMealIdeaDraggableId = (draggableId) => {
	const match = typeof draggableId === 'string' && draggableId.match(/^mealIdea-(\d+|ungrouped)-(\d+)$/);
	if(!match) {
		return null;
	}
	return {
		groupId: match[1] === UNGROUPED ? null : Number(match[1]),
		ideaId: Number(match[2]),
	};
};

const mealIdeaGroupId = (droppableId) => {
	const match = typeof droppableId === 'string' && droppableId.match(/^mealIdeaGroup-(\d+)$/);
	return match ? Number(match[1]) : null;
};

/**
 * What a drop between meal idea groups does, or null when it does nothing there.
 *
 * The groups share the calendar's drag type so their ideas can reach its slots, which also lets a
 * grocery item or a meal be dropped on a group; those are ignored, as is anything landing in
 * Ungrouped, which is sorted by name and holds only what is in no group.
 */
export const mealIdeaGroupDrop = (result) => {
	const idea = parseMealIdeaDraggableId(result.draggableId);
	const to = result.destination ? mealIdeaGroupId(result.destination.droppableId) : null;
	if(!idea || to === null) {
		return null;
	}
	if(idea.groupId === to) {
		return { kind: 'reorder', groupId: to, startIndex: result.source.index, endIndex: result.destination.index };
	}
	if(idea.groupId === null) {
		return { kind: 'add', ideaId: idea.ideaId, groupId: to, index: result.destination.index };
	}
	return { kind: 'move', ideaId: idea.ideaId, fromGroupId: idea.groupId, toGroupId: to, index: result.destination.index };
};
